// CutsceneTimeline.tsx - the Cutscenes mode (SF23.3): a real multi-track
// scrubber authoring the schema SF23.1 defined (engine/content/schema/
// cutscene.ts), replacing the old disabled stub in Animation Studio
// ("Timeline sequencing arrives with the cutscene editor" - it has).
// Persists through the mode-document store as kind 'cutscenes'
// (CutsceneLibraryDoc). Exports: CutsceneRoute.
import { useFrame, useThree } from '@react-three/fiber';
import { PerspectiveCamera } from '@react-three/drei';
import { Bloom, ChromaticAberration, EffectComposer, Noise, Vignette } from '@react-three/postprocessing';
import { BlendFunction } from 'postprocessing';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Vector2 } from 'three';
import {
  activeActorClips,
  activeCameraKey,
  CUTSCENE_MAX_DURATION,
  samplePost,
  validateCutscene,
  type ActorKey,
  type AudioCue,
  type CameraKey,
  type Cutscene,
  type PostKeyframe,
  type PostState,
  type Signal,
} from '../../engine/content/schema/cutscene';
import {
  CLIP_GROUPS,
  createPrototypeInstance,
  isPrototypeClip,
  loadPrototypeGltf,
  Viewport,
  type PrototypeClip,
  type PrototypeInstance,
} from '../../engine';
import { EdConfirmDialog } from './editor-panels';
import { useEditorHost } from './EditorHost';
import { FieldNumber, FieldSelect, FieldText } from './fieldPrimitives';
import { M2Label, M2Panel } from './editor-modes2';
import { useModeDocument } from './useModeDocument';

/** The 'cutscenes' mode document: every authored cutscene in the project. */
export type CutsceneLibraryDoc = { cutscenes: Cutscene[] };
export const CUTSCENE_LIBRARY_DOC_FALLBACK: CutsceneLibraryDoc = { cutscenes: [] };

type TrackId = 'camera' | 'actors' | 'post' | 'audio' | 'signals';
const TRACKS: { id: TrackId; label: string }[] = [
  { id: 'camera', label: 'Camera' },
  { id: 'actors', label: 'Actors' },
  { id: 'post', label: 'Post FX' },
  { id: 'audio', label: 'Audio' },
  { id: 'signals', label: 'Signals' },
];

export function uniqueId(base: string, taken: string[]): string {
  if (!taken.includes(base)) return base;
  let n = 2;
  while (taken.includes(base + '_' + n)) n += 1;
  return base + '_' + n;
}

export function blankCutscene(id: string): Cutscene {
  return {
    id,
    duration: 3,
    interruptible: 'after-first-view',
    tracks: { camera: [], actors: [], post: [], audio: [], signals: [] },
  };
}

/* ── Preview: a real Canvas driven by the same pure sampling functions the
 * runtime CutscenePlayer uses, so authoring is no longer entirely blind. Not
 * `CutscenePlayer` itself, since that drives a live game scene through
 * `FirstPersonController`'s camera-override ref; here the camera just snaps
 * straight to each sampled key every frame - an honest, WYSIWYG reading of
 * the authored keys rather than a guess at the runtime's own pan easing,
 * which the schema does not actually specify. ────────────────────────── */

/** Every distinct actorId referenced anywhere on the actor track, in first-
 * appearance order - the schema supports more than one performer per scene. */
function useCutsceneActorIds(cutscene: Cutscene): string[] {
  return useMemo(() => {
    const seen: string[] = [];
    for (const key of cutscene.tracks.actors) {
      if (!seen.includes(key.actorId)) seen.push(key.actorId);
    }
    return seen;
  }, [cutscene.tracks.actors]);
}

function CutsceneActorRig({ index, total, clip }: { index: number; total: number; clip: PrototypeClip }) {
  const [instance, setInstance] = useState<PrototypeInstance | null>(null);

  useEffect(() => {
    let cancelled = false;
    let created: PrototypeInstance | null = null;
    loadPrototypeGltf()
      .then((gltf) => {
        if (cancelled) return;
        created = createPrototypeInstance(gltf, { bodyColor: '#8c93a8', accentColor: '#2142d4' });
        created.fitHeight(1.8);
        created.root.traverse((node) => {
          const mesh = node as unknown as { isMesh?: boolean; castShadow?: boolean; receiveShadow?: boolean };
          if (mesh.isMesh) {
            mesh.castShadow = true;
            mesh.receiveShadow = true;
          }
        });
        setInstance(created);
      })
      .catch((err: unknown) => console.error('[cutscene preview] rig failed to load:', err));
    return () => {
      cancelled = true;
      created?.dispose();
    };
  }, []);

  useEffect(() => {
    instance?.play(clip, { loop: true, fade: 0.15 });
  }, [instance, clip]);

  useFrame((_, dt) => instance?.update(dt));

  const spread = total > 1 ? (index - (total - 1) / 2) * 1.2 : 0;
  return instance ? <primitive object={instance.root} position={[spread, 0, 0]} /> : null;
}

/** Snaps the Canvas's own default camera to the sampled camera key every
 * frame; before the first key (or on `release`) it just leaves the camera at
 * whatever the Canvas was given, a stable default framing of the actors. */
function CutsceneCameraRig({ cameraKey }: { cameraKey: CameraKey | null }) {
  const { camera } = useThree();
  useFrame(() => {
    if (!cameraKey?.approach || !cameraKey.lookAt) return;
    camera.position.set(...cameraKey.approach);
    camera.lookAt(...cameraKey.lookAt);
  });
  return null;
}

/** The same post-processing stack the runtime's `PostEffects` applies, but
 * fed by props instead of the global engine settings store - that store
 * persists to localStorage on every change, so driving it from here while
 * scrubbing would overwrite the player's own real graphics settings. */
function CutscenePostFx({ post }: { post: PostState }) {
  const chromaticOffset = useMemo(() => new Vector2(0.000455, 0.000315), []);
  return (
    <EffectComposer multisampling={0}>
      <Bloom mipmapBlur intensity={post.bloomIntensity ?? 0} luminanceThreshold={0.42} luminanceSmoothing={0.38} />
      <Vignette eskil={false} offset={0.22} darkness={post.vignetteAmount ?? 0} />
      <Noise premultiply opacity={post.grainOpacity ?? 0} blendFunction={BlendFunction.SOFT_LIGHT} />
      {post.chromaticAberration ? <ChromaticAberration offset={chromaticOffset} /> : <></>}
    </EffectComposer>
  );
}

function CutscenePreviewClock({ playing, onTick }: { playing: boolean; onTick: (dt: number) => void }) {
  useFrame((_, dt) => {
    if (playing) onTick(dt);
  });
  return null;
}

function CutscenePreview({
  cutscene, t, setT, playing, setPlaying,
}: {
  cutscene: Cutscene;
  t: number;
  setT: (t: number) => void;
  playing: boolean;
  setPlaying: (v: boolean) => void;
}) {
  const actorIds = useCutsceneActorIds(cutscene);
  const activeClips = useMemo(() => activeActorClips(cutscene, t), [cutscene, t]);
  const cameraKey = useMemo(() => activeCameraKey(cutscene, t), [cutscene, t]);
  const post = useMemo(() => samplePost(cutscene, t), [cutscene, t]);

  useEffect(() => {
    if (t >= cutscene.duration && playing) setPlaying(false);
  }, [t, cutscene.duration, playing, setPlaying]);

  function handleTick(dt: number) {
    setT(Math.min(cutscene.duration, t + dt));
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ position: 'relative', height: 260, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--ss-border-soft)', background: '#0a0a0c' }}>
        <Viewport shadows>
          <PerspectiveCamera makeDefault fov={50} position={[0, 1.6, 4]} />
          <color attach="background" args={['#0a0a0c']} />
          <hemisphereLight args={['#bcd2ff', '#202833', 1.1]} />
          <directionalLight color="#fff4e6" intensity={2} position={[3, 6, 4]} castShadow />
          <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
            <circleGeometry args={[8, 48]} />
            <meshStandardMaterial color="#181b1f" roughness={0.92} />
          </mesh>
          {actorIds.map((id, i) => {
            const clipId = activeClips[id];
            const clip = clipId && isPrototypeClip(clipId) ? clipId : 'Idle_Loop';
            return <CutsceneActorRig key={id} index={i} total={actorIds.length} clip={clip} />;
          })}
          <CutsceneCameraRig cameraKey={cameraKey} />
          <CutscenePostFx post={post} />
          <CutscenePreviewClock playing={playing} onTick={handleTick} />
        </Viewport>
        <span style={{
          position: 'absolute', top: 10, left: 10, background: 'var(--ss-glass)', backdropFilter: 'blur(8px)',
          border: '1px solid var(--ss-border-soft)', borderRadius: 999, padding: '5px 12px',
          fontSize: 10.5, color: 'var(--ss-text-2)', pointerEvents: 'none',
        }}>
          Camera {cameraKey ? cameraKey.mode : 'unforced'} · {actorIds.length === 0 ? 'no actors' : `${actorIds.length} actor${actorIds.length === 1 ? '' : 's'}`}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
        <button
          className="ss-btn ss-btn--ghost ss-btn--sm"
          onClick={() => setPlaying(!playing)}
        >{playing ? '⏸ Pause' : '▶ Play'}</button>
        <button
          className="ss-btn ss-btn--ghost ss-btn--sm"
          onClick={() => { setT(0); setPlaying(false); }}
        >⏮ Restart</button>
        <input
          type="range" min={0} max={Math.max(cutscene.duration, 0.001)} step={0.01} value={t}
          onChange={(e) => { setPlaying(false); setT(Number(e.target.value)); }}
          style={{ flex: 1 }}
          className="ed-range"
        />
        <span style={{ fontSize: 11, color: 'var(--ss-text-3)', fontVariantNumeric: 'tabular-nums', width: 84, textAlign: 'right' }}>
          {t.toFixed(2)}s / {cutscene.duration.toFixed(2)}s
        </span>
      </div>
    </div>
  );
}

/** A key on any track, tagged with which track it came from, for a single
 * selection model across five differently-shaped arrays. */
type SelectedKey =
  | { track: 'camera'; index: number }
  | { track: 'actors'; index: number }
  | { track: 'post'; index: number }
  | { track: 'audio'; index: number }
  | { track: 'signals'; index: number };

export function CutsceneRoute() {
  const { data: storedDoc, setData: saveDoc, loading: docLoading } =
    useModeDocument<CutsceneLibraryDoc>('cutscenes', CUTSCENE_LIBRARY_DOC_FALLBACK);
  const [sel, setSel] = useState(0);
  const [selectedKey, setSelectedKey] = useState<SelectedKey | null>(null);
  const [previewT, setPreviewT] = useState(0);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const hydrated = useRef(false);
  const [cutscenes, setCutscenesState] = useState<Cutscene[]>([]);
  if (!docLoading && !hydrated.current) {
    hydrated.current = true;
    setCutscenesState(storedDoc.cutscenes);
  }

  // setCutscenesState only ever computes the next value here - React may
  // call a state updater more than once per update (Strict Mode, concurrent
  // rendering), so scheduling the network-bound saveDoc from inside one was
  // a purity violation, currently harmless only because the debounced saver
  // coalesces duplicate calls. The actual save is a separate effect below.
  const setCutscenes = (next: Cutscene[] | ((list: Cutscene[]) => Cutscene[])) => {
    setCutscenesState((list) => (typeof next === 'function' ? next(list) : next));
  };

  // Every cutscene must clear validateCutscene (duration within the 6s cap,
  // every key within [0, duration], every non-release camera key with an
  // approach/lookAt) before this document goes to the backend at all - the
  // banner below used to be the only thing standing between an invalid
  // cutscene and a 200 from the save endpoint.
  useEffect(() => {
    if (!hydrated.current) return;
    const allValid = cutscenes.every((c) => {
      try {
        validateCutscene(c);
        return true;
      } catch {
        return false;
      }
    });
    if (!allValid) return;
    saveDoc({ cutscenes });
  }, [cutscenes, saveDoc]);

  const cutscene = cutscenes[sel] as Cutscene | undefined;
  let validationError: string | null = null;
  if (cutscene) {
    try {
      validateCutscene(cutscene);
    } catch (err) {
      validationError = err instanceof Error ? err.message : 'Invalid cutscene.';
    }
  }

  useEffect(() => {
    setPreviewT(0);
    setPreviewPlaying(false);
  }, [cutscene?.id]);

  function addCutscene() {
    const id = uniqueId('cutscene', cutscenes.map((c) => c.id));
    setCutscenes((list) => [...list, blankCutscene(id)]);
    setSel(cutscenes.length);
    setSelectedKey(null);
  }

  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null);
  function removeCutscene(index: number) {
    setCutscenes((list) => list.filter((_, i) => i !== index));
    setSel((s) => Math.max(0, s >= index ? s - 1 : s));
    setSelectedKey(null);
  }

  function patchCutscene(patch: Partial<Cutscene>) {
    setCutscenes((list) => list.map((c, i) => (i === sel ? { ...c, ...patch } : c)));
  }

  /** The id is the presumed reference point for anything (Logic, a future
   * trigger action) that fires this cutscene, so a rename is deliberate and
   * validated rather than a plain text field. */
  function renameCutscene(rawId: string) {
    if (!cutscene) return;
    const nextId = rawId.trim();
    if (!nextId || nextId === cutscene.id) return;
    if (cutscenes.some((c) => c.id !== cutscene.id && c.id === nextId)) return;
    patchCutscene({ id: nextId });
  }

  function patchTracks(patch: Partial<Cutscene['tracks']>) {
    if (!cutscene) return;
    patchCutscene({ tracks: { ...cutscene.tracks, ...patch } });
  }

  return (
    <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
      <M2Panel side="left" width={220}>
        <M2Label action={
          <button className="ss-btn ss-btn--ghost" onClick={addCutscene} style={{ padding: '3px 9px', fontSize: 11, borderRadius: 6 }}>+ New</button>
        }>Cutscenes</M2Label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '0 8px' }}>
          {cutscenes.length === 0 && (
            <span style={{ padding: '10px 8px', fontSize: 12, color: 'var(--ss-text-3)' }}>No cutscenes yet.</span>
          )}
          {cutscenes.map((c, i) => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <button
                onClick={() => { setSel(i); setSelectedKey(null); }}
                style={{
                  flex: 1, textAlign: 'left', padding: '7px 9px', borderRadius: 7, fontSize: 12.5,
                  background: i === sel ? 'var(--ss-accent-soft)' : 'transparent',
                  color: i === sel ? 'var(--ss-accent)' : 'var(--ss-text-2)', border: 'none', cursor: 'pointer',
                }}
              >{c.id}</button>
              <button
                onClick={() => setPendingDeleteIndex(i)}
                aria-label={`Delete ${c.id}`}
                title="Delete"
                style={{ padding: '4px 6px', fontSize: 11, color: 'var(--ss-text-3)', background: 'none', border: 'none', cursor: 'pointer' }}
              >×</button>
            </div>
          ))}
        </div>
      </M2Panel>

      {pendingDeleteIndex !== null && (
        <EdConfirmDialog
          title={`Delete "${cutscenes[pendingDeleteIndex]?.id}"?`}
          body="This permanently removes every track and key in this cutscene. There is no undo."
          confirmLabel="Delete"
          onConfirm={() => { removeCutscene(pendingDeleteIndex); setPendingDeleteIndex(null); }}
          onCancel={() => setPendingDeleteIndex(null)}
        />
      )}

      <div style={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>
        {!cutscene ? (
          <div style={{ padding: 40, color: 'var(--ss-text-3)', fontSize: 13 }}>
            Select a cutscene, or create one to start authoring its camera, actor, post-FX, audio and signal tracks.
          </div>
        ) : (
          <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 36px 72px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
              <input
                key={cutscene.id}
                className="ss-input"
                defaultValue={cutscene.id}
                title="Cutscene id - what anything that triggers this cutscene refers to it by"
                onBlur={(e) => renameCutscene(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                style={{ fontFamily: 'var(--ss-display)', fontSize: 20, fontWeight: 500, padding: '4px 9px', borderRadius: 8, width: 220 }}
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ss-text-3)' }}>
                Duration
                <input
                  className="ss-input" type="number" min={0} max={CUTSCENE_MAX_DURATION} step={0.1}
                  value={cutscene.duration}
                  onChange={(e) => {
                    const raw = Number(e.target.value);
                    if (!Number.isFinite(raw)) return;
                    patchCutscene({ duration: Math.min(CUTSCENE_MAX_DURATION, Math.max(0, raw)) });
                  }}
                  style={{ width: 64, padding: '4px 8px', fontSize: 12 }}
                />
                s (max {CUTSCENE_MAX_DURATION}s)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ss-text-3)' }}>
                Interruptible
                <select
                  className="ss-input"
                  value={cutscene.interruptible}
                  onChange={(e) => patchCutscene({ interruptible: e.target.value as Cutscene['interruptible'] })}
                  style={{ padding: '4px 8px', fontSize: 12 }}
                >
                  <option value="never">Never</option>
                  <option value="after-first-view">After first view</option>
                </select>
              </label>
            </div>

            {validationError && (
              <div style={{ padding: '8px 12px', marginBottom: 16, borderRadius: 8, background: 'rgba(220,80,80,0.12)', color: 'var(--ss-danger, #e06060)', fontSize: 12.5 }}>
                {validationError}
              </div>
            )}

            <CutscenePreview
              cutscene={cutscene}
              t={previewT}
              setT={setPreviewT}
              playing={previewPlaying}
              setPlaying={setPreviewPlaying}
            />

            <TimelineTracks
              cutscene={cutscene}
              selectedKey={selectedKey}
              onSelectKey={setSelectedKey}
              onPatchTracks={patchTracks}
              playheadT={previewT}
              onScrub={(t) => { setPreviewPlaying(false); setPreviewT(t); }}
            />

            {selectedKey && (
              <KeyEditor
                cutscene={cutscene}
                selected={selectedKey}
                onPatchTracks={patchTracks}
                onSelectKey={setSelectedKey}
                onDeselect={() => setSelectedKey(null)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function TimelineTracks({
  cutscene, selectedKey, onSelectKey, onPatchTracks, playheadT, onScrub,
}: {
  cutscene: Cutscene;
  selectedKey: SelectedKey | null;
  onSelectKey: (key: SelectedKey) => void;
  onPatchTracks: (patch: Partial<Cutscene['tracks']>) => void;
  playheadT: number;
  onScrub: (t: number) => void;
}) {
  const rulerRef = useRef<HTMLDivElement>(null);

  function timeFromClientX(clientX: number): number {
    const el = rulerRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const frac = rect.width > 0 ? (clientX - rect.left) / rect.width : 0;
    const t = Math.max(0, Math.min(cutscene.duration, frac * cutscene.duration));
    return Math.round(t * 20) / 20; // snap to 0.05s
  }

  function addKeyAt(track: TrackId, t: number) {
    // Every track array is kept sorted ascending by `t` (activeActorClips /
    // activeCameraKey / samplePost rely on this). The new key rarely sorts to
    // the end, so its post-sort index is found by identity rather than
    // assumed to be the pre-insertion length - otherwise selection lands on
    // whichever pre-existing key the new one displaced.
    if (track === 'camera') {
      const key: CameraKey = { t, mode: 'take', approach: [0, 1.6, 0], lookAt: [0, 1, -1] };
      const sorted = [...cutscene.tracks.camera, key].sort((a, b) => a.t - b.t);
      onPatchTracks({ camera: sorted });
      onSelectKey({ track: 'camera', index: sorted.indexOf(key) });
    } else if (track === 'actors') {
      const key: ActorKey = { t, actorId: 'player', clipId: 'Idle_Loop' };
      const sorted = [...cutscene.tracks.actors, key].sort((a, b) => a.t - b.t);
      onPatchTracks({ actors: sorted });
      onSelectKey({ track: 'actors', index: sorted.indexOf(key) });
    } else if (track === 'post') {
      const key: PostKeyframe = { t, bloomIntensity: 1 };
      const sorted = [...cutscene.tracks.post, key].sort((a, b) => a.t - b.t);
      onPatchTracks({ post: sorted });
      onSelectKey({ track: 'post', index: sorted.indexOf(key) });
    } else if (track === 'audio') {
      const key: AudioCue = { t, cue: 'cue' };
      const sorted = [...cutscene.tracks.audio, key].sort((a, b) => a.t - b.t);
      onPatchTracks({ audio: sorted });
      onSelectKey({ track: 'audio', index: sorted.indexOf(key) });
    } else {
      const key: Signal = { t, event: 'signal' };
      const sorted = [...cutscene.tracks.signals, key].sort((a, b) => a.t - b.t);
      onPatchTracks({ signals: sorted });
      onSelectKey({ track: 'signals', index: sorted.indexOf(key) });
    }
  }

  const activeClips = activeActorClips(cutscene, playheadT);
  const activeCam = activeCameraKey(cutscene, playheadT);
  const playheadLeft = cutscene.duration > 0 ? (playheadT / cutscene.duration) * 100 : 0;

  return (
    <div style={{ position: 'relative', border: '1px solid var(--ss-border-soft)', borderRadius: 10, overflow: 'hidden' }}>
      <div
        ref={rulerRef}
        data-testid="cutscene-ruler"
        role="slider"
        aria-label="Playhead"
        aria-valuemin={0}
        aria-valuemax={cutscene.duration}
        aria-valuenow={playheadT}
        onClick={(e) => onScrub(timeFromClientX(e.clientX))}
        style={{ position: 'relative', height: 22, background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid var(--ss-border-soft)', cursor: 'pointer' }}
      >
        {Array.from({ length: Math.floor(cutscene.duration) + 1 }, (_, s) => (
          <span key={s} style={{
            position: 'absolute', left: `${cutscene.duration > 0 ? (s / cutscene.duration) * 100 : 0}%`,
            top: 3, fontSize: 9, color: 'var(--ss-text-3)', transform: 'translateX(-50%)', pointerEvents: 'none',
          }}>{s}s</span>
        ))}
      </div>
      {/* playhead - spans the ruler and every track lane */}
      <div style={{
        position: 'absolute', top: 0, bottom: 0, left: `${playheadLeft}%`, width: 1.5,
        background: 'var(--ss-accent)', pointerEvents: 'none', zIndex: 1,
        boxShadow: '0 0 6px var(--ss-accent-border)',
      }} />
      <div style={{
        position: 'absolute', top: 0, left: `${playheadLeft}%`, transform: 'translateX(-50%)',
        width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent',
        borderTop: '5px solid var(--ss-accent)', pointerEvents: 'none', zIndex: 1,
      }} />
      {TRACKS.map(({ id, label }) => {
        const keys: { t: number }[] = cutscene.tracks[id];
        return (
          <div key={id} style={{ display: 'flex', borderBottom: '1px solid var(--ss-border-soft)' }}>
            <div style={{ width: 84, flexShrink: 0, padding: '8px 10px', fontSize: 11, color: 'var(--ss-text-3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</div>
            <div
              role="button"
              tabIndex={0}
              aria-label={`Add a ${label.toLowerCase()} key`}
              onClick={(e) => {
                if (e.target !== e.currentTarget) return;
                addKeyAt(id, timeFromClientX(e.clientX));
              }}
              style={{ position: 'relative', flex: 1, height: 30, cursor: 'copy' }}
            >
              {keys.map((key, i) => {
                const isSelected = selectedKey?.track === id && selectedKey.index === i;
                const left = cutscene.duration > 0 ? (key.t / cutscene.duration) * 100 : 0;
                return (
                  <button
                    key={i}
                    onClick={(e) => { e.stopPropagation(); onSelectKey({ track: id, index: i } as SelectedKey); }}
                    title={`t=${key.t}s`}
                    style={{
                      position: 'absolute', left: `${left}%`, top: '50%', transform: 'translate(-50%, -50%)',
                      width: 11, height: 11, borderRadius: 3, cursor: 'pointer',
                      background: isSelected ? 'var(--ss-accent)' : 'var(--ss-text-2)',
                      border: '1px solid var(--ss-bg-2)',
                    }}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
      <div style={{ padding: '6px 10px', fontSize: 10.5, color: 'var(--ss-text-3)' }}>
        At t={playheadT.toFixed(2)}s: {Object.keys(activeClips).length > 0
          ? Object.entries(activeClips).map(([a, c]) => `${a} plays ${c}`).join(', ')
          : 'no actor clip active'}; camera {activeCam ? activeCam.mode : 'unforced'}.
      </div>
    </div>
  );
}

function KeyEditor({
  cutscene, selected, onPatchTracks, onSelectKey, onDeselect,
}: {
  cutscene: Cutscene;
  selected: SelectedKey;
  onPatchTracks: (patch: Partial<Cutscene['tracks']>) => void;
  onSelectKey: (key: SelectedKey) => void;
  onDeselect: () => void;
}) {
  function removeKey() {
    const list = cutscene.tracks[selected.track] as { t: number }[];
    onPatchTracks({ [selected.track]: list.filter((_, i) => i !== selected.index) } as Partial<Cutscene['tracks']>);
    onDeselect();
  }

  function patchKey(patch: Record<string, unknown>) {
    // Re-sort after every patch, since `t` (the sort key) can change here -
    // every track array must stay sorted ascending by `t` for
    // activeActorClips/activeCameraKey/samplePost to sample it correctly.
    // The patched key is tracked by identity so selection follows it to its
    // new index rather than staying on whatever key now sits at the old one.
    const list = cutscene.tracks[selected.track] as unknown as Record<string, unknown>[];
    const patched = { ...list[selected.index], ...patch };
    const next = list.map((k, i) => (i === selected.index ? patched : k)).sort((a, b) => (a.t as number) - (b.t as number));
    onPatchTracks({ [selected.track]: next } as unknown as Partial<Cutscene['tracks']>);
    const newIndex = next.indexOf(patched);
    if (newIndex !== selected.index) onSelectKey({ track: selected.track, index: newIndex } as SelectedKey);
  }

  const key = (cutscene.tracks[selected.track] as { t: number }[])[selected.index];
  if (!key) return null;

  return (
    <div style={{ marginTop: 16, padding: 16, borderRadius: 10, background: 'var(--ss-bg-2)', border: '1px solid var(--ss-border-soft)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--ss-text-3)' }}>
          {selected.track} key
        </span>
        <button className="ss-btn ss-btn--ghost" onClick={removeKey} style={{ padding: '3px 9px', fontSize: 11, borderRadius: 6 }}>Delete</button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <FieldNumber label="t (s)" value={key.t} min={0} max={cutscene.duration} step={0.05} onChange={(v) => patchKey({ t: v })} />
        {selected.track === 'camera' && <CameraFields keyValue={key as CameraKey} onChange={patchKey} cutscene={cutscene} />}
        {selected.track === 'actors' && <ActorFields keyValue={key as ActorKey} onChange={patchKey} />}
        {selected.track === 'post' && <PostFields keyValue={key as PostKeyframe} onChange={patchKey} />}
        {selected.track === 'audio' && <FieldText label="Cue" value={(key as AudioCue).cue} onChange={(v) => patchKey({ cue: v })} />}
        {selected.track === 'signals' && <FieldText label="Event" value={(key as Signal).event} onChange={(v) => patchKey({ event: v })} />}
      </div>
    </div>
  );
}

function CameraFields({ keyValue, onChange, cutscene }: {
  keyValue: CameraKey;
  onChange: (patch: Record<string, unknown>) => void;
  cutscene: Cutscene;
}) {
  const actorIds = useCutsceneActorIds(cutscene);
  return (
    <>
      <FieldSelect
        label="Mode" value={keyValue.mode}
        options={[['take', 'Take'], ['hold', 'Hold'], ['release', 'Release']]}
        onChange={(v) => onChange({ mode: v })}
      />
      {keyValue.mode !== 'release' && (
        <>
          <FieldVector3 label="Approach" value={keyValue.approach ?? [0, 0, 0]} onChange={(v) => onChange({ approach: v })} />
          <FieldVector3 label="Look at" value={keyValue.lookAt ?? [0, 0, 0]} onChange={(v) => onChange({ lookAt: v })} />
          <FieldCheckbox
            label="Instant (hard cut)"
            checked={keyValue.instant ?? false}
            onChange={(v) => onChange({ instant: v })}
          />
          <FieldText
            label="Track actor id"
            value={keyValue.trackActorId ?? ''}
            onChange={(v) => onChange({ trackActorId: v.trim() === '' ? undefined : v })}
            suggestions={actorIds}
          />
          {keyValue.trackActorId && (
            <>
              <FieldNumber
                label="Track distance" value={keyValue.trackDistance ?? 1.1} min={0} step={0.1}
                onChange={(v) => onChange({ trackDistance: v })}
              />
              <FieldNumber
                label="Track height offset" value={keyValue.trackHeightOffset ?? 0} step={0.1}
                onChange={(v) => onChange({ trackHeightOffset: v })}
              />
            </>
          )}
        </>
      )}
    </>
  );
}

function ActorFields({ keyValue, onChange }: { keyValue: ActorKey; onChange: (patch: Record<string, unknown>) => void }) {
  const { project } = useEditorHost();
  const actorIds = useMemo(() => ['player', ...project.characters.map((c) => c.id)], [project.characters]);
  const clipIds = useMemo(() => Object.values(CLIP_GROUPS).flat(), []);
  return (
    <>
      <FieldText label="Actor id" value={keyValue.actorId} onChange={(v) => onChange({ actorId: v })} suggestions={actorIds} />
      <FieldText label="Clip id" value={keyValue.clipId} onChange={(v) => onChange({ clipId: v })} suggestions={clipIds} />
    </>
  );
}

function PostFields({ keyValue, onChange }: { keyValue: PostKeyframe; onChange: (patch: Record<string, unknown>) => void }) {
  return (
    <>
      <FieldNumber label="Bloom" value={keyValue.bloomIntensity ?? 0} min={0} max={4} step={0.1} onChange={(v) => onChange({ bloomIntensity: v })} />
      <FieldNumber label="Vignette" value={keyValue.vignetteAmount ?? 0} min={0} max={1} step={0.05} onChange={(v) => onChange({ vignetteAmount: v })} />
      <FieldNumber label="Grain" value={keyValue.grainOpacity ?? 0} min={0} max={1} step={0.05} onChange={(v) => onChange({ grainOpacity: v })} />
      <FieldCheckbox
        label="Chromatic aberration"
        checked={keyValue.chromaticAberration ?? false}
        onChange={(v) => onChange({ chromaticAberration: v })}
      />
    </>
  );
}

function FieldCheckbox({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: 'var(--ss-text-3)', justifyContent: 'flex-end' }}>
      &nbsp;
      <span style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 0', fontSize: 12, color: 'var(--ss-text-2)' }}>
        <button role="switch" aria-checked={checked} className={'ss-switch' + (checked ? ' is-on' : '')} onClick={() => onChange(!checked)} aria-label={label} />
        {label}
      </span>
    </label>
  );
}

function FieldVector3({ label, value, onChange }: { label: string; value: [number, number, number]; onChange: (v: [number, number, number]) => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: 'var(--ss-text-3)' }}>
      {label}
      <div style={{ display: 'flex', gap: 4 }}>
        {(['x', 'y', 'z'] as const).map((axis, i) => (
          <input
            key={axis} className="ss-input" type="number" step={0.1} value={value[i]}
            onChange={(e) => {
              const next: [number, number, number] = [...value];
              next[i] = Number(e.target.value);
              onChange(next);
            }}
            style={{ width: 52, padding: '5px 6px', fontSize: 11.5 }}
          />
        ))}
      </div>
    </div>
  );
}
